import React, { useMemo, useState, memo } from 'react';

import { Card, Empty, Space, Tag, Typography } from 'antd';

import {
    buildStatusColorMap,
    normalizeStatusKey
} from '../utils/taskManager';
import { hasPersonAssignee } from '../utils/tmWorkflow';

import './TaskManagerKanban.css';

const { Text } = Typography;

const StatusTag = ({ status, colorMap }) => {
    const color =
        colorMap[normalizeStatusKey(status)] || '#1677ff';

    return (
        <Tag
            style={{
                margin: 0,
                color: '#fff',
                background: color,
                borderColor: color,
                fontWeight: 600,
                whiteSpace: 'normal'
            }}
        >
            {status || 'Без статуса'}
        </Tag>
    );
};

const KanbanCard = memo(
    ({
        task,
        color,
        colorMap,
        canEdit,
        canDrag,
        isDragging,
        statusMoved,
        updated,
        cfPreview,
        onTaskClick,
        onDragStart,
        onDragEnd
    }) => (
        <Card
            size="small"
            hoverable
            className={`tm-kanban-card${
                isDragging ? ' is-dragging' : ''
            }${statusMoved ? ' is-status-moved' : ''}${
                updated && !statusMoved ? ' is-updated' : ''
            }`}
            style={{ borderLeftColor: color }}
            draggable={canEdit && canDrag}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onClick={() => onTaskClick?.(task)}
        >
            <div className="tm-kanban-card-top">
                <Text type="secondary">
                    #{task.id} ·{' '}
                    {task.task_number ||
                        task.external_id ||
                        '—'}
                </Text>
                <StatusTag
                    status={task.status_name}
                    colorMap={colorMap}
                />
            </div>

            <Text strong className="tm-kanban-card-title">
                {task.title || 'Без названия'}
            </Text>

            <Text type="secondary" className="tm-kanban-card-meta">
                {[
                    task.assigned_user_fio,
                    task.region_name,
                    task.news_type_name
                ]
                    .filter(Boolean)
                    .join(' · ') || '—'}
            </Text>

            {cfPreview?.length ? (
                <Space wrap size={[4, 4]} style={{ marginTop: 6 }}>
                    {cfPreview.map(item => (
                        <Tag key={item.system_name}>
                            {item.human_name}:{' '}
                            {String(item.value).slice(0, 20)}
                        </Tag>
                    ))}
                </Space>
            ) : null}
        </Card>
    )
);

const TaskManagerKanban = ({
    tasks = [],
    statuses = [],
    columnStatusNames = null,
    colorMap: colorMapProp,
    statusAnimIds = new Set(),
    updatedAnimIds = new Set(),
    canEdit = false,
    onTaskClick,
    onStatusChange,
    getCfPreview,
    getAllowedTargets
}) => {
    const [dragTaskId, setDragTaskId] = useState(null);
    const [dragOverStatus, setDragOverStatus] =
        useState(null);
    const [dragAllowed, setDragAllowed] = useState(null);

    const colorMap = useMemo(
        () => colorMapProp || buildStatusColorMap(statuses),
        [colorMapProp, statuses]
    );

    const columns = useMemo(() => {
        const statusNames = Array.isArray(columnStatusNames)
            ? columnStatusNames
            : statuses.map(item => item.name);

        const byStatus = new Map();
        for (const task of tasks) {
            const key = task.status_name || '';
            if (!byStatus.has(key)) {
                byStatus.set(key, []);
            }
            byStatus.get(key).push(task);
        }

        const ordered = [...statusNames];
        for (const key of byStatus.keys()) {
            if (key && !ordered.includes(key)) {
                ordered.push(key);
            }
        }
        if (byStatus.has('') && !ordered.includes('')) {
            ordered.push('');
        }

        return ordered.map(name => ({
            key: name || '__none__',
            name: name || 'Без статуса',
            color:
                colorMap[normalizeStatusKey(name)] ||
                '#1677ff',
            tasks: byStatus.get(name || '') || []
        }));
    }, [tasks, statuses, colorMap, columnStatusNames]);

    const isDropAllowed = statusName => {
        if (dragAllowed === null) return true;
        if (!Array.isArray(dragAllowed)) return true;
        return (
            dragAllowed.includes(statusName || null) ||
            dragAllowed.includes(statusName || '')
        );
    };

    const onDropToStatus = async statusName => {
        if (!dragTaskId) {
            setDragOverStatus(null);
            setDragAllowed(null);
            return;
        }

        const task = tasks.find(
            item => String(item.id) === String(dragTaskId)
        );

        const allowed = dragAllowed;
        setDragTaskId(null);
        setDragOverStatus(null);
        setDragAllowed(null);

        if (!task) {
            return;
        }

        const nextStatus = statusName || null;
        if (
            (task.status_name || null) ===
            (nextStatus || null)
        ) {
            return;
        }

        if (
            Array.isArray(allowed) &&
            !allowed.includes(nextStatus) &&
            !allowed.includes(statusName || '')
        ) {
            return;
        }

        if (onStatusChange) {
            await onStatusChange(task.id, nextStatus);
        }
    };

    if (!columns.length) {
        return (
            <Empty description="Статусы не настроены" />
        );
    }

    return (
        <div className="tm-kanban-board">
            {columns.map(column => {
                const dropName =
                    column.key === '__none__'
                        ? ''
                        : column.name;
                const allowed = isDropAllowed(dropName);
                return (
                    <div
                        key={column.key}
                        className={`tm-kanban-column${
                            dragOverStatus === column.key
                                ? ' is-over'
                                : ''
                        }${
                            dragTaskId && !allowed
                                ? ' is-forbidden'
                                : ''
                        }`}
                        style={{
                            borderTopColor: column.color
                        }}
                        onDragOver={event => {
                            if (dragTaskId && !allowed) {
                                event.dataTransfer.dropEffect =
                                    'none';
                                return;
                            }
                            event.preventDefault();
                            setDragOverStatus(column.key);
                        }}
                        onDragLeave={() => {
                            if (
                                dragOverStatus === column.key
                            ) {
                                setDragOverStatus(null);
                            }
                        }}
                        onDrop={event => {
                            event.preventDefault();
                            if (dragTaskId && !allowed) {
                                setDragTaskId(null);
                                setDragOverStatus(null);
                                setDragAllowed(null);
                                return;
                            }
                            onDropToStatus(dropName);
                        }}
                    >
                        <div className="tm-kanban-column-header">
                            <Space>
                                <span
                                    style={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        background:
                                            column.color,
                                        display:
                                            'inline-block'
                                    }}
                                />
                                <Text strong>
                                    {column.name}
                                </Text>
                                <Tag color={column.color}>
                                    {column.tasks.length}
                                </Tag>
                            </Space>
                        </div>

                        <div className="tm-kanban-column-body">
                            {column.tasks.length === 0 ? (
                                <div className="tm-kanban-empty">
                                    Перетащите сюда
                                </div>
                            ) : (
                                column.tasks.map(task => (
                                    <KanbanCard
                                        key={task.id}
                                        task={task}
                                        color={column.color}
                                        colorMap={colorMap}
                                        canEdit={canEdit}
                                        canDrag={hasPersonAssignee(
                                            task
                                        )}
                                        isDragging={
                                            String(
                                                dragTaskId
                                            ) ===
                                            String(task.id)
                                        }
                                        statusMoved={statusAnimIds.has(
                                            task.id
                                        )}
                                        updated={updatedAnimIds.has(
                                            task.id
                                        )}
                                        cfPreview={getCfPreview?.(
                                            task
                                        )}
                                        onTaskClick={
                                            onTaskClick
                                        }
                                        onDragStart={event => {
                                            if (
                                                !canEdit ||
                                                !hasPersonAssignee(
                                                    task
                                                )
                                            ) {
                                                event.preventDefault();
                                                return;
                                            }
                                            event.dataTransfer.effectAllowed =
                                                'move';
                                            setDragTaskId(
                                                task.id
                                            );
                                            const targets =
                                                getAllowedTargets?.(
                                                    task
                                                );
                                            setDragAllowed(
                                                targets ===
                                                    undefined
                                                    ? null
                                                    : targets
                                            );
                                        }}
                                        onDragEnd={() => {
                                            setDragTaskId(
                                                null
                                            );
                                            setDragOverStatus(
                                                null
                                            );
                                            setDragAllowed(
                                                null
                                            );
                                        }}
                                    />
                                ))
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default TaskManagerKanban;
